<template>
  <div
    v-if="canSelect || isCalculating"
    class="d-flex align-center"
    data-testid="draw-selection-actions"
  >
    <SoButton
      v-if="canSelect"
      title="Select winners"
      type="primary"
      data-testid="select-winners-btn"
      @click="showSelectDialog = true"
    />

    <div
      v-else-if="isCalculating"
      class="d-flex align-center text-medium-emphasis"
      data-testid="selection-calculating"
    >
      <SoProgress indeterminate :size="20" class="mr-3" />
      Selecting winners. This can take a few hours — check back later.
    </div>

    <SoDialog
      v-model="showSelectDialog"
      card-title="Select winners?"
      :max-width="480"
      data-testid="select-winners-dialog"
    >
      <p>
        This closes the draw and selects winners — entries can no longer change.
        It runs in the background and can take a few hours — you can leave this
        page and check back later.
      </p>
      <div class="d-flex justify-end mt-4">
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          @click="showSelectDialog = false"
        />
        <SoButton
          title="Select winners"
          type="primary"
          large
          :loading="closeMutation.isPending"
          data-testid="select-winners-confirm-btn"
          @click="confirmSelect"
        />
      </div>
    </SoDialog>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoProgress from "@/components/SoProgress.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import errorHandler from "@/services/ErrorService/errorHandler"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import { PrizeDrawStatus } from "@/api/backofficeRest/generated/model"

  export default defineComponent({
    components: { SoButton, SoDialog, SoProgress },
    props: {
      drawId: {
        type: String,
        required: true,
      },
      status: {
        type: String as PropType<PrizeDrawStatus | null>,
        default: null,
      },
    },
    emits: ["changed"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        closeMutation: LoyaltyMutator.closeDraw(),
        showSelectDialog: false,
      }
    },
    computed: {
      // "Select winners" closes the draw + selects; only once entries have closed.
      canSelect(): boolean {
        return this.status === PrizeDrawStatus.ENTRY_CLOSED
      },
      isCalculating(): boolean {
        return this.status === PrizeDrawStatus.CALCULATING_WINNERS
      },
    },
    methods: {
      async confirmSelect() {
        try {
          await this.closeMutation.mutateAsync({ drawId: this.drawId })
        } catch (error) {
          errorHandler.showCaughtError(
            error,
            "Failed to start winner selection. Please try again.",
          )
          return
        }
        this.snackbar.addSuccess("Winner selection started")
        this.showSelectDialog = false
        this.$emit("changed")
      },
    },
  })
</script>
